import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { appeler } from '../../api/client.ts';
import { Carte, Chargement, Erreur } from '../../composants/communs.tsx';
import { useSession } from '../../composants/session.tsx';
import { CANAUX, LIBELLE_CANAL, type Canal } from './types.ts';

/**
 * Où un produit se vend, boutique par boutique — RG-196, RG-032.
 *
 * Une ligne par boutique de la portée, y compris celles qui ne le vendent
 * pas : c'est ici qu'on les y ajoute. N'afficher que les boutiques servies
 * rendrait l'ajout impossible.
 *
 * Le prix saisi est une SURCHARGE locale. Laissé vide, le prix commun du
 * produit s'applique — c'est ce que dit l'espace réservé.
 */

interface LigneBoutique {
  boutique_id: string;
  code: string;
  nom: string;
  active: boolean;
  est_depot: boolean;
  canaux: Canal[];
  surcharges: Partial<Record<Canal, number>> | null;
}

type Brouillon = Record<
  string,
  { canaux: Canal[]; prix: Partial<Record<Canal, string>> }
>;

export function FicheDistribution({
  produitId,
  designation,
  onFerme,
}: {
  produitId: string;
  designation: string;
  onFerme: () => void;
}): ReactNode {
  const cache = useQueryClient();
  const { peut } = useSession();
  const [brouillon, setBrouillon] = useState<Brouillon>({});

  const { data, isPending, error } = useQuery({
    queryKey: ['distribution', produitId],
    queryFn: () =>
      appeler<{ canaux_catalogue: Canal[]; boutiques: LigneBoutique[] }>(
        `/produits/${produitId}/distribution`,
      ),
  });

  /*
    Les canaux viennent du SERVEUR, pas d'une constante.

    L'ecran proposait les trois canaux quel que soit le produit : cocher un
    canal absent de son catalogue faisait cocher une case que le serveur
    refuse ensuite (RG-028). Mieux vaut ne pas la montrer.
  */
  const canauxDuProduit = data?.canaux_catalogue ?? [];
  const lignes = data?.boutiques ?? [];

  const modifiable = peut('produit.distribuer');

  const mutation = useMutation({
    mutationFn: () =>
      appeler(`/produits/${produitId}/distribution`, {
        methode: 'PUT',
        corps: {
          // On n'envoie que les boutiques RÉELLEMENT touchées : celles qu'on
          // n'a pas modifiées gardent la leur, côté serveur.
          boutiques: Object.entries(brouillon).map(([boutiqueId, etat]) => ({
            boutique_id: boutiqueId,
            canaux: etat.canaux,
            // Un champ vide n'est PAS un prix de zéro : il signifie « suivre
            // le prix commun », donc il ne doit pas partir du tout.
            prix: Object.fromEntries(
              Object.entries(etat.prix)
                .map(([canal, montant]): [string, number] => [canal, Number(montant)])
                .filter(([, montant]) => Number.isFinite(montant) && montant > 0),
            ),
          })),
        },
      }).then(() => undefined),
    onSuccess: () => {
      setBrouillon({});
      void cache.invalidateQueries({ queryKey: ['distribution', produitId] });
      // Le catalogue affiche le prix : il change aussi.
      void cache.invalidateQueries({ queryKey: ['produits'] });
    },
  });

  function etatDe(ligne: LigneBoutique): { canaux: Canal[]; prix: Partial<Record<Canal, string>> } {
    const dejaTouchee = brouillon[ligne.boutique_id];
    if (dejaTouchee !== undefined) {
      return dejaTouchee;
    }
    return {
      canaux: ligne.canaux,
      prix: Object.fromEntries(
        Object.entries(ligne.surcharges ?? {}).map(([canal, montant]) => [
          canal,
          String(montant),
        ]),
      ),
    };
  }

  function modifier(
    ligne: LigneBoutique,
    changement: Partial<{ canaux: Canal[]; prix: Partial<Record<Canal, string>> }>,
  ): void {
    const actuel = etatDe(ligne);
    setBrouillon({
      ...brouillon,
      [ligne.boutique_id]: { ...actuel, ...changement },
    });
  }

  const enAttente = Object.keys(brouillon).length;

  return (
    <Carte
      titre={`Où vendre « ${designation} »`}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Fermer
        </button>
      }
    >
      <p className="entete__sous" style={{ marginBottom: 12 }}>
        Décochez un canal pour retirer le produit d’une boutique. Le retrait
        atteint les téléphones à leur prochaine synchronisation. Un prix laissé
        vide suit le prix commun.
      </p>

      <Erreur erreur={error} />
      <Erreur erreur={mutation.error} />

      {isPending ? (
        <Chargement />
      ) : (
        <div className="tableau-defilant">
          <table>
            <thead>
              <tr>
                <th>Boutique</th>
                {canauxDuProduit.map((canal) => (
                  <th key={canal}>{LIBELLE_CANAL[canal]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignes.map((ligne) => {
                const etat = etatDe(ligne);
                return (
                  <tr key={ligne.boutique_id}>
                    <td>
                      <span className="mono">{ligne.code}</span> {ligne.nom}
                      {ligne.est_depot && (
                        <div className="entete__sous">dépôt — ne vend pas</div>
                      )}
                      {!ligne.active && <div className="entete__sous">désactivée</div>}
                    </td>

                    {canauxDuProduit.map((canal) => {
                      const vendu = etat.canaux.includes(canal);
                      return (
                        <td key={canal}>
                          <label style={{ fontWeight: 400, marginBottom: 6 }}>
                            <input
                              type="checkbox"
                              style={{ width: 'auto', marginRight: 6 }}
                              checked={vendu}
                              disabled={!modifiable}
                              aria-label={`${LIBELLE_CANAL[canal]} — ${ligne.code}`}
                              onChange={() => {
                                modifier(ligne, {
                                  canaux: vendu
                                    ? etat.canaux.filter((element) => element !== canal)
                                    : [...etat.canaux, canal],
                                });
                              }}
                            />
                            Vendu
                          </label>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            disabled={!modifiable || !vendu}
                            value={etat.prix[canal] ?? ''}
                            placeholder="prix commun"
                            aria-label={`Prix ${LIBELLE_CANAL[canal]} — ${ligne.code}`}
                            onChange={(evenement) => {
                              modifier(ligne, {
                                prix: { ...etat.prix, [canal]: evenement.target.value },
                              });
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modifiable && (
        <div className="ligne" style={{ marginTop: 14 }}>
          <button
            type="button"
            disabled={enAttente === 0 || mutation.isPending}
            onClick={() => {
              mutation.mutate();
            }}
          >
            {mutation.isPending
              ? 'Enregistrement…'
              : `Enregistrer ${String(enAttente)} boutique${enAttente > 1 ? 's' : ''}`}
          </button>
          {enAttente > 0 && (
            <button
              type="button"
              className="bouton--discret"
              onClick={() => {
                setBrouillon({});
              }}
            >
              Annuler les changements
            </button>
          )}
        </div>
      )}
    </Carte>
  );
}

export { CANAUX };
